import { ModelType } from '@livv/models';
import { AnalysisDisplayPublicWithPremiumStatus } from '@livv/models/firebaseFunctions';
import { Container, Typography, Grid, Box, SxProps } from '@mui/material';
import { FC, ReactNode } from 'react';
import { ModelTypeLogo } from '../search';
import ClipBoardButton from '@components/ClipboardButton';
import ContentWrapper from '@components/ContentWrapper';
import DocumentHeader from '@components/DocumentHeader';
import DocumentSubtitles from '@components/DocumentSubtitles';
import HeadMeta from '@components/HeadMeta';
import PrintButton from '@components/PrintButton';
import Tags from '@components/Tags';

const topInfoSx: SxProps = {
    '&:before': {
        backgroundColor: 'grey.100',
        content: "''",
        display: 'block',
        height: 0.5,
        left: 0,
        maxHeight: 72,
        position: 'absolute',
        top: 0,
        width: '100%',
        zIndex: 0,
    },
    position: 'relative',
};

interface DocumentLayoutProps {
    analysesWithPremiumStatus?: AnalysisDisplayPublicWithPremiumStatus;
    breadcrumbs?: ReactNode;
    canonicalUrl?: string;
    children: ReactNode;
    customPrintHandler?: () => void;
    documentId: string;
    fullHeight?: boolean;
    headTitle?: string;
    lightHeader?: boolean;
    maxContentWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    modelType?: ModelType;
    pageDescription?: string;
    printable?: boolean;
    showPrintButton?: boolean;
    subtitle?: string;
    subtitleElements?: string[];
    tags?: string[];
    title: string;
    topInfosContainer?: ReactNode;
}

const DocumentLayout: FC<DocumentLayoutProps> = ({
    analysesWithPremiumStatus,
    breadcrumbs,
    canonicalUrl,
    children,
    customPrintHandler,
    documentId,
    headTitle,
    fullHeight = false,
    lightHeader,
    maxContentWidth = 'lg',
    modelType,
    printable,
    showPrintButton,
    subtitle,
    subtitleElements,
    tags,
    topInfosContainer,
    title,
    pageDescription,
}) => (
    <Box
        component="article"
        sx={[fullHeight && { display: 'flex', flexDirection: 'column', height: '100%' }]}
    >
        <HeadMeta
            canonicalUrl={canonicalUrl}
            description={pageDescription}
            pageTitle={headTitle ?? title}
        />
        <DocumentHeader lightHeader={lightHeader} maxContentWidth={maxContentWidth}>
            {breadcrumbs}
            {modelType && <ModelTypeLogo modelType={modelType} sx={{ mb: 2 }} />}
            <Grid container>
                <Grid sm={printable ? 9 : 12} item>
                    {title && (
                        <Typography
                            data-test-id="main-title"
                            mb={1}
                            sx={{ color: 'grey.800' }}
                            variant="h1"
                        >
                            {title}
                            <ClipBoardButton textToCopy={title} />
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography sx={{ color: 'grey.800', my: 1 }} gutterBottom>
                            {subtitle}
                        </Typography>
                    )}
                </Grid>
                {printable && modelType && showPrintButton && (
                    <Grid
                        sm={3}
                        sx={{ alignItems: 'flex-start', justifyContent: 'flex-end', mt: 3 }}
                        container
                        item
                    >
                        <PrintButton
                            analysesWithPremiumStatus={analysesWithPremiumStatus}
                            customButtonHandler={customPrintHandler}
                            documentId={documentId}
                            documentName={title}
                            documentType={modelType}
                            plainButton
                        />
                    </Grid>
                )}
            </Grid>
            {subtitleElements && <DocumentSubtitles elements={subtitleElements} />}
            {tags && (
                <Tags
                    hitId={documentId}
                    sx={{
                        color: 'grey.800',
                        mt: 1,
                    }}
                    tags={tags}
                    variant="outlined"
                />
            )}
        </DocumentHeader>
        {topInfosContainer && (
            <Grid sx={topInfoSx} container>
                <Container maxWidth="lg" sx={{ zIndex: 1 }}>
                    <Grid
                        sx={{ backgroundColor: 'background.paper', borderRadius: 1.5, p: 2.5 }}
                        container
                    >
                        {topInfosContainer}
                    </Grid>
                </Container>
            </Grid>
        )}
        <ContentWrapper dataTestId={modelType} maxContentWidth={maxContentWidth}>
            <Box>{children}</Box>
        </ContentWrapper>
    </Box>
);
export default DocumentLayout;
